import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  Row,
  Col,
  Statistic,
  Progress,
  Table,
  Typography,
  Button,
  Space,
  Tag,
  message,
  Modal,
  Form,
  InputNumber,
  Divider,
} from "antd";
import { ArrowLeftOutlined, SettingOutlined } from "@ant-design/icons";
import { ITaskOrderProgress, IProductionAllocation } from "../../types/progressTracking.type";
import { api } from "../../api/api";
import { useAuth } from "../../contexts/auth.context";

const { Title } = Typography;

export default function TaskOrderProgress() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { authState } = useAuth();
  const [progress, setProgress] = useState<ITaskOrderProgress | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [priorityModalOpen, setPriorityModalOpen] = useState<boolean>(false);
  const [form] = Form.useForm();

  const fetchProgress = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/progress/task-order/${id}/progress`);
      setProgress(response.data.data);
    } catch (error) {
      console.error("Error fetching progress:", error);
      message.error("Ошибка при получении данных прогресса");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchProgress();
    }
  }, [id]);

  const handleUpdatePriority = async (values: { priority: number }) => {
    try {
      await api.put(`/progress/task-order/${id}/priority`, values);
      message.success("Приоритет обновлен");
      setPriorityModalOpen(false);
      fetchProgress();
    } catch (error: any) {
      console.error("Error updating priority:", error);
      message.error(error.response?.data?.error || "Ошибка при обновлении приоритета");
    }
  };

  const allocationColumns = [
    {
      title: "Дата выделения",
      dataIndex: "allocationDate",
      key: "allocationDate",
    },
    {
      title: "Количество",
      dataIndex: "allocatedQuantity",
      key: "allocatedQuantity",
    },
    {
      title: "Статус",
      key: "status",
      render: (_: any, record: IProductionAllocation) => (
        <Tag color={record.isDelivered ? "green" : "blue"}>
          {record.isDelivered ? "Выдано" : "На складе"}
        </Tag>
      ),
    },
    {
      title: "Заказ продажи",
      dataIndex: "orderId",
      key: "orderId",
      render: (orderId: string) => orderId ? (
        <Button 
          type="link" 
          onClick={() => navigate(`/order/${orderId}`)}
        >
          {orderId}
        </Button>
      ) : "-",
    },
    {
      title: "Ответственный",
      dataIndex: "ownerName",
      key: "ownerName",
    },
  ];

  if (loading) {
    return <div>Загрузка...</div>;
  }

  if (!progress) {
    return <div>Данные не найдены</div>;
  }

  const { taskOrder } = progress;

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ marginBottom: 16 }}>
        <Button 
          icon={<ArrowLeftOutlined />} 
          onClick={() => navigate("/task-orders")}
        >
          Назад
        </Button>
        <Title level={2} style={{ margin: 0 }}>
          Прогресс заказа-наряда
        </Title>
      </Space>

      <Card style={{ marginBottom: 24 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Statistic
              title="Клиент"
              value={taskOrder.clientName}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Statistic
              title="Продукция"
              value={taskOrder.produceName}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Statistic
              title="Общее количество"
              value={taskOrder.total}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Statistic
              title="Статус"
              value={taskOrder.status}
            />
          </Col>
        </Row>
      </Card>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} md={12}>
          <Card title="Прогресс производства">
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                <span>Произведено: {taskOrder.produced} / {taskOrder.total}</span>
                <span>{taskOrder.productionProgress}%</span>
              </div>
              <Progress 
                percent={taskOrder.productionProgress} 
                status={taskOrder.productionProgress === 100 ? "success" : "active"}
              />
            </div>
            <Statistic
              title="Осталось произвести"
              value={taskOrder.total - taskOrder.produced}
            />
          </Card>
        </Col>
        
        <Col xs={24} md={12}>
          <Card title="Прогресс выдачи">
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                <span>Выдано: {taskOrder.delivered} / {taskOrder.total}</span>
                <span>{taskOrder.deliveryProgress}%</span>
              </div>
              <Progress 
                percent={taskOrder.deliveryProgress} 
                status={taskOrder.deliveryProgress === 100 ? "success" : "active"}
                strokeColor={taskOrder.deliveryProgress === 100 ? "#52c41a" : "#1890ff"}
              />
            </div>
            <Statistic
              title="Осталось выдать"
              value={taskOrder.total - taskOrder.delivered}
            />
          </Card>
        </Col>
      </Row>

      <Card 
        title="История выделений продукции"
        extra={
          <Space>
            <Tag color={taskOrder.priority === 0 ? "red" : taskOrder.priority <= 5 ? "orange" : "green"}>
              Приоритет: {taskOrder.priority === 0 ? "Высокий" : taskOrder.priority <= 5 ? "Средний" : "Низкий"}
            </Tag>
            {canEdit(authState.user?.role) && (
              <Button 
                icon={<SettingOutlined />} 
                onClick={() => {
                  form.setFieldsValue({ priority: taskOrder.priority });
                  setPriorityModalOpen(true);
                }}
              >
                Изменить приоритет
              </Button>
            )}
          </Space>
        }
      >
        <Table
          columns={allocationColumns}
          dataSource={progress.allocations}
          rowKey="_id"
          pagination={false}
          size="small"
        />
      </Card>

      <Modal
        title="Изменить приоритет"
        open={priorityModalOpen}
        onCancel={() => setPriorityModalOpen(false)}
        onOk={() => form.submit()}
      >
        <Form form={form} layout="vertical" onFinish={handleUpdatePriority}>
          <Form.Item
            name="priority"
            label="Приоритет"
            rules={[{ required: true, message: "Введите приоритет" }]}
            extra="0 = Высокий, 1-5 = Средний, 6+ = Низкий"
          >
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

// Вспомогательная функция для проверки прав
function canEdit(role?: string): boolean {
  return role === "admin" || role === "manager";
}
